import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  ButtonIcon,
  ButtonSelect,
  Chip,
  Icon,
  InlineMessage,
  InputField,
  Link,
  Menu,
  type MenuOption,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';

import type { AgentPolicyCondition } from '@repo/api/schemas/agentPolicy.schema';

import { TOKENS } from '../../../../shared/constants';
import { useFilterOptions } from '../../hooks/useAgentPolicy';
import {
  buildConditionMenuOptions,
  isRowIncomplete,
  mapApiConditionsToRows,
} from '../../utils/conditionFormUtils';
import {
  type ConditionRow,
  createEmptyConditionRow,
  getOperatorLabel,
  OPERATOR_OPTIONS,
} from '../../utils/policyFormUtils';

import { TRIGGER_LABELS, type TriggerType } from './TriggerSelectContent';

export { type ConditionRow, OPERATOR_OPTIONS };

const VALUE_PATTERN = /^[a-zA-Z0-9\-_]*$/;
const MAX_CONDITIONS = 15;

interface ConditionContentProps {
  onConditionChange?: (data: { conditions: ConditionRow[] }) => void;
  previousConditions?: AgentPolicyCondition;
  /** When true, show "Incomplete condition" on rows with any empty field */
  saveAttempted?: boolean;
  /** Persisted conditions from parent (when user returns from step 2) */
  initialConditions?: ConditionRow[];
  /** Navigate back to the trigger selection screen */
  onBack?: () => void;
  /** Which trigger was selected — controls the header label */
  triggerType?: TriggerType;
}

export default function ConditionContent({
  onConditionChange,
  previousConditions,
  saveAttempted = false,
  initialConditions,
  onBack,
  triggerType = 'agent_registered',
}: ConditionContentProps = {}) {
  const intl = useIntl();
  const [conditionSearchTerm, setConditionSearchTerm] = useState('');
  const [duplicateConditionIds, setDuplicateConditionIds] = useState<
    Set<string>
  >(new Set());
  const [invalidValueIds, setInvalidValueIds] = useState<Set<string>>(
    new Set()
  );
  const hasInitializedFromPreviousRef = useRef(false);
  const [conditions, setConditions] = useState<ConditionRow[]>(() => {
    if (initialConditions?.length) {
      return initialConditions;
    }
    const rows = mapApiConditionsToRows(previousConditions);
    return rows.length > 0 ? rows : [createEmptyConditionRow()];
  });

  // Fetch condition (label) options on page load so all condition Menus have API values
  const { data: apiConditionOptions = [] } = useFilterOptions(
    'labels',
    conditionSearchTerm,
    {
      enabled: true,
    }
  );

  // Keep all option values we've ever seen (from API or from any condition row) so the menu
  // shows every option regardless of whether it's currently used or was used earlier.
  const allKnownOptionValuesRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const set = allKnownOptionValuesRef.current;
    apiConditionOptions.forEach((o) => {
      if (o.value) {
        set.add(String(o.value));
      }
    });
    conditions.forEach((c) => {
      if (c.attribute?.trim()) {
        set.add(c.attribute.trim());
      }
    });
  }, [apiConditionOptions, conditions]);

  const conditionOptions = useMemo(
    () =>
      buildConditionMenuOptions(
        previousConditions,
        conditions,
        apiConditionOptions,
        conditionSearchTerm,
        allKnownOptionValuesRef.current
      ),
    [apiConditionOptions, conditionSearchTerm, conditions, previousConditions]
  );

  // Notify parent on mount so step count stays in sync (e.g. when no previousConditions)
  useEffect(() => {
    if (!previousConditions) {
      notifyChange(conditions);
    }
  }, []);

  // Prefill from API when edit page loads (once only)
  useEffect(() => {
    if (!previousConditions?.conditions?.length) {
      return;
    }
    if (hasInitializedFromPreviousRef.current) {
      return;
    }
    hasInitializedFromPreviousRef.current = true;
    const rows = mapApiConditionsToRows(previousConditions);
    const toSet = rows.length > 0 ? rows : [createEmptyConditionRow()];
    setConditions(toSet);
    onConditionChange?.({ conditions: toSet });
  }, [previousConditions, onConditionChange]);

  const notifyChange = (updatedConditions: ConditionRow[]) => {
    onConditionChange?.({ conditions: updatedConditions });
  };
  const removeCondition = (id: string) => {
    setDuplicateConditionIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    const updated = conditions.filter((c: ConditionRow) => c.id !== id);
    setConditions(updated);
    notifyChange(updated);
  };

  const addCondition = () => {
    if (conditions.length >= MAX_CONDITIONS) {
      return;
    }
    const updated = [...conditions, createEmptyConditionRow()];
    setConditions(updated);
    notifyChange(updated);
  };

  const setConditionOperatorKey = (
    conditionId: string,
    operatorKey: string
  ) => {
    const updated = conditions.map((c: ConditionRow) =>
      c.id === conditionId ? { ...c, operator: operatorKey } : c
    );
    setConditions(updated);
    notifyChange(updated);
  };

  const handleLabelChange = (
    conditionId: string,
    selectedOption: MenuOption | null
  ) => {
    if (!selectedOption) {
      return;
    }

    const selectedValue = (selectedOption.value as string)?.trim();
    if (!selectedValue) {
      return;
    }

    const usedByOtherRow = conditions.some(
      (c) =>
        c.id !== conditionId && (c.attribute ?? '').trim() === selectedValue
    );
    if (usedByOtherRow) {
      setDuplicateConditionIds((prev) => new Set(prev).add(conditionId));
      return;
    }

    setDuplicateConditionIds((prev) => {
      const next = new Set(prev);
      next.delete(conditionId);
      return next;
    });
    const updated = conditions.map((c: ConditionRow) =>
      c.id === conditionId
        ? { ...c, attribute: selectedOption.value as string }
        : c
    );
    setConditions(updated);
    notifyChange(updated);
  };

  const setConditionValue = (conditionId: string, text: string) => {
    setInvalidValueIds((prev) => {
      const next = new Set(prev);
      if (text && !VALUE_PATTERN.test(text)) {
        next.add(conditionId);
      } else {
        next.delete(conditionId);
      }
      return next;
    });
    const updated = conditions.map((c: ConditionRow) =>
      c.id === conditionId ? { ...c, value: text } : c
    );
    setConditions(updated);
    notifyChange(updated);
  };

  return (
    <main className='PolicyForm-detail'>
      {/* Panel header: back link + trigger */}
      <Box
        className={cx(
          'PolicyForm-panel-surface',
          'PolicyForm-panel-header',
          'PolicyForm-panel-header--condition'
        )}>
        <Box className='PolicyForm-panel-header-conditionBack'>
          <Link
            kind='button'
            text={intl.formatMessage({
              id: 'JjutHV',
              defaultMessage: 'When should this policy run?',
              description: 'Step title link in condition panel header',
            })}
            onClick={() => onBack?.()}
            prefixIconKind='ArrowLeft'
          />
        </Box>
        <Box className='PolicyForm-panel-header-conditionTrigger'>
          <Icon
            kind={triggerType === 'labels_change' ? 'Tag' : 'Certification'}
            color={TOKENS.CF_INF_BLD}
          />
          <Typography
            kind='h2'
            desktop='h2-d'
            mobile='h2-d'
            color={TOKENS.CF_SEC_BLD}>
            {TRIGGER_LABELS[triggerType]}
          </Typography>
        </Box>
      </Box>

      {/* Panel body: conditions only (no All Agents / Only Specific Agents) */}
      <Box
        className={cx(
          'PolicyForm-panel-surface',
          'PolicyForm-panel-body',
          'PolicyForm-panel-body--condition'
        )}>
        <Typography
          kind='body2'
          color={TOKENS.CF_SEC_BLD}
          className='PolicyForm-conditionsIntro'>
          <FormattedMessage
            id='jPShVp'
            defaultMessage='Apply this policy only to agents that match the conditions below.'
            description='Conditions intro text in condition panel body'
          />
        </Typography>
        <div className='PolicyForm-conditionsRows'>
          {/* Condition rows: each row = [condition | operator | value | delete | AND chip (if not last)] */}
          {conditions.map((condition, index) => (
            <div key={condition.id} className='PolicyForm-conditionRow'>
              <div className='PolicyForm-conditionRow-grid'>
                <div
                  className='PolicyForm-conditionRow-attribute'
                  style={{ flex: '3 1 0%' }}>
                  <Menu
                    isPortalEnabled
                    options={conditionOptions}
                    className='PolicyForm-fullWidth'
                    trigger={
                      <ButtonSelect
                        kind='rectangle'
                        size='large'
                        color='neutral'
                        label={
                          condition.attribute?.trim() ||
                          intl.formatMessage({
                            id: '8ijlCN',
                            defaultMessage: 'Select or type to add',
                            description: 'Condition attribute placeholder',
                          })
                        }
                        isVisualCueEnabled
                        className='PolicyForm-conditionTriggerBtn'
                        classNames={{
                          labelClassName: 'PolicyForm-conditionTriggerLabel',
                        }}
                      />
                    }
                    onChange={(opt: MenuOption) =>
                      opt && handleLabelChange(condition.id, opt)
                    }
                    shouldCloseMenuOnItemClick
                    minOptionsForSearch={0}
                    onSearchTermChange={setConditionSearchTerm}
                    classNames={{
                      triggerClassName: 'PolicyForm-conditionMenuTrigger',
                      dropdownClassName: 'PolicyForm-menuDropdown',
                      customMenuClassName: 'PolicyForm-menuDropdown',
                    }}
                  />
                  {duplicateConditionIds.has(condition.id) && (
                    <Typography
                      kind='body3'
                      color={TOKENS.CF_CRT_BLD}
                      className='PolicyForm-conditionRow-duplicate'>
                      <FormattedMessage
                        id='F7u5wN'
                        defaultMessage='This key is already added to this agent.'
                        description='Duplicate condition error message'
                      />
                    </Typography>
                  )}
                </div>
                <div
                  className='PolicyForm-conditionRow-operator'
                  style={{ flex: '1.5 1 0%' }}>
                  <Menu
                    isPortalEnabled
                    options={OPERATOR_OPTIONS}
                    className='PolicyForm-fullWidth'
                    trigger={
                      <ButtonSelect
                        kind='rectangle'
                        size='large'
                        color='neutral'
                        label={
                          condition.operator
                            ? getOperatorLabel(condition.operator)
                            : intl.formatMessage({
                                id: 'hfdubD',
                                defaultMessage: 'Select operator',
                                description: 'Operator dropdown placeholder',
                              })
                        }
                        isVisualCueEnabled
                        className='PolicyForm-operatorBtn'
                      />
                    }
                    onChange={(opt: MenuOption) =>
                      opt &&
                      setConditionOperatorKey(condition.id, String(opt.value))
                    }
                    shouldCloseMenuOnItemClick
                    classNames={{
                      triggerClassName: 'PolicyForm-conditionMenuTrigger',
                      dropdownClassName: 'PolicyForm-menuDropdown',
                      customMenuClassName: 'PolicyForm-menuDropdown',
                    }}
                  />
                </div>
                <div
                  className='PolicyForm-conditionRow-value'
                  style={{ flex: '3 1 0%' }}>
                  <InputField
                    name={`condition-value-${condition.id}`}
                    placeholder={intl.formatMessage({
                      id: 'tgf+Hy',
                      defaultMessage: 'Enter value',
                      description: 'Condition value input placeholder',
                    })}
                    size='large'
                    value={condition.value ?? ''}
                    setValue={(val) =>
                      setConditionValue(condition.id, String(val ?? ''))
                    }
                    className='PolicyForm-fullWidth'
                    isCritical={invalidValueIds.has(condition.id)}
                    CriticalHelperText={
                      <InlineMessage
                        text={intl.formatMessage({
                          defaultMessage:
                            'Invalid format. Only use letters, numbers, hyphens (-), or underscores (_)',
                          description: 'Invalid condition value format error',
                          id: 'WcM+RA',
                        })}
                        colorTheme='critical'
                        size='small'
                        leftIcon={<Icon kind='AlertCriticalFilled' />}
                      />
                    }
                  />
                </div>
                <div className='PolicyForm-conditionRow-deleteWrap'>
                  <ButtonIcon
                    size='medium'
                    kind='ghost'
                    className='PolicyForm-delete-btn'
                    icon={<Icon kind='Delete' size='small' />}
                    ariaLabel={intl.formatMessage({
                      id: 'G8NBV2',
                      defaultMessage: 'Remove condition',
                      description: 'Remove condition row',
                    })}
                    onClick={() => removeCondition(condition.id)}
                  />
                </div>
                {/* AND chip in line with row (except after last row) */}
                <div className='PolicyForm-conditionRow-andWrap'>
                  {index < conditions.length - 1 ? (
                    <Chip
                      kind='outlined'
                      label='AND'
                      size='xSmall'
                      isInteractionDisabled
                      className='PolicyForm-and-chip'
                    />
                  ) : null}
                </div>
              </div>
              {saveAttempted && isRowIncomplete(condition) && (
                <Typography
                  kind='body2'
                  color={TOKENS.CF_CRT_BLD}
                  className='PolicyForm-conditionRow-incomplete'>
                  <FormattedMessage
                    id='nfziTi'
                    defaultMessage='Incomplete condition'
                    description='Shown when a condition row has empty required fields'
                  />
                </Typography>
              )}
            </div>
          ))}
        </div>

        <Button
          kind='outlined'
          size='small'
          leftIcon={<Icon kind='Add' size='small' />}
          onClick={addCondition}
          className='PolicyForm-addConditionBtn'
          isDisabled={conditions.length >= MAX_CONDITIONS}>
          <FormattedMessage
            id='Wu62/M'
            defaultMessage='Add Another Condition'
            description='Add another condition button'
          />
        </Button>
        {conditions.length >= MAX_CONDITIONS && (
          <Typography
            kind='body2'
            color={TOKENS.CF_SAV_MED}
            className='PolicyForm-addConditionLimit'>
            <FormattedMessage
              id='mb4bje'
              defaultMessage="Limit reached: You've used the maximum amount of labels for this policy"
              description='Max conditions limit reached message'
            />
          </Typography>
        )}
      </Box>
    </main>
  );
}
